<script lang="ts">
  import type { OrderGroup } from "@shared/types/OrderGroup";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import dateHelpers from "@shared/filters/tiz_date.js";
  import Arrow from "@shared/svelte/Components/Arrow.svelte";
  import ActionRequiredBadge from "@retailer/svelte/Shared/ActionRequiredBadge.svelte";
  import OrderService from "@shared/services/order.js";
  import { download } from "@shared/svelte/Modules/Download.svelte";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import PunchoutSessionStore from "@retailer/svelte/stores/PunchoutSessionStore";
  import retailerContext from "@modern/services/retailer_context";
  import RetailerDistributorStore from "@retailer/svelte/stores/RetailerDistributorStore";
  import {
    orderStatusSummary,
    statusRed,
    statusGreen,
    statusGray,
  } from "@shared/services/order_status_service";

  export let orderGroup: OrderGroup;
  export let mobileMode: boolean;

  const retailer = retailerContext.get();

  let retailerName =
    orderGroup && orderGroup.retailer_data_at_purchase
      ? orderGroup.retailer_data_at_purchase.name
      : retailer.name;

  $: aooEnabledMap = $RetailerDistributorStore.reduce((map, rd) => {
    map[rd.distributor.id] = rd.is_aoo_enabled;
    return map;
  }, {});
  $: displayStateText = (displayState: string, totalLocations: number, count: number): string =>
    `${displayState} ${totalLocations > 1 ? `(${count} of ${totalLocations})` : ""}`;
</script>

<div
  class="order-index__order grid-row margin-bottom--small margin-top--small background-white margin-left--none margin-right--none"
  style="margin-left: 0; margin-right: 0"
>
  <div order-group-id={orderGroup.id} id="order_group_{orderGroup.id}">
    <header
      class="order-index__order-header order_group_show_link padding--smaller padding--small--sm"
      order-group-id={orderGroup.id}
    >
      <section class="fizz-flex-row fizz-align-content-start fizz-inset-vertical-8">
        <div class="fizz-flex-item">
          <div class="fizz-flex-row-responsive" class:fizz-stack-4={$LayoutStore.isMobile}>
            <span
              class="fizz-text-strong fizz-text-large"
              class:fizz-stack-4={$LayoutStore.isMobile}
              >{dateHelpers.noDateNoTimeNoTz(orderGroup.submitted_at)}</span
            >
            {#if orderGroup.sf_order_import === true}
              <div><span class="fizz-badge $spacing-16">SevenFifty order</span></div>
            {/if}
          </div>
          <div class="fizz-text-medium">Order Group #{orderGroup.id}</div>
          {#if orderGroup.punchout_order_request}
            <div class="fizz-text-medium">
              {$PunchoutSessionStore?.procurement_system_name || "Punchout"} PO #{orderGroup
                .punchout_order_request.purchase_order_number}
            </div>
          {/if}
        </div>
        <div class="fizz-flex-item">
          <div class="align-right" class:fizz-stack-4={$LayoutStore.isMobile}>
            <span class="fizz-text-strong fizz-text-large">{retailerName}</span>
          </div>
          <div class="align-right">
            <span class="fizz-text-large">
              {orderGroup.submitted_by_user.first_name}
              {orderGroup.submitted_by_user.last_name}
            </span>
          </div>
        </div>
      </section>
    </header>

    {#each orderGroup.orders as order}
      <UIRouterLink
        useDiv={true}
        stateName="layout.order_details"
        params={{ orderGroupId: orderGroup.id, orderId: order.id }}
        classes={`order_group_show_scroll_link_${order.id} cursor-pointer order-index__order-item background-white padding--smaller padding--small--sm`}
        id={`order_${order.id}`}
      >
        <div class="grid-row" data-order-id={order.id}>
          <div class={`grid-col ${$LayoutStore.isMobile ? "c4-10" : "c7-10"}`}>
            <span
              class="default-body--regular default-subheader--regular--md display-inline-block notranslate"
              >{order.distributor.name}</span
            >
          </div>
          <div
            class={`grid-col ${
              $LayoutStore.isMobile ? "c6-10" : "c3-10"
            } fizz-flex-row-inline fizz-justify-end`}
          >
            <div class="fizz-flex-row fizz-justify-end fizz-stack-4">
              <p class="fizz-text-strong align-right" style="margin-right: unset;">
                {#if order.manual_order_action_required && aooEnabledMap[order.distributor.id]}
                  <ActionRequiredBadge />
                {:else if Object.keys(orderStatusSummary(order)).length > 0}
                  {#each Object.keys(orderStatusSummary(order)) as displayState}
                    <p
                      class:fizz-text-negative={statusRed(displayState)}
                      class:fizz-text-positive={statusGreen(order, displayState)}
                      class:fizz-text-subdued={statusGray(order, displayState)}
                      >{displayStateText(
                        displayState,
                        orderStatusSummary(order)[displayState].totalLocations,
                        orderStatusSummary(order)[displayState].count,
                      )}</p
                    >
                  {/each}
                {:else}
                  <span
                    class="capitalize"
                    class:fizz-text-negative={statusRed(order.display_state)}
                    class:fizz-text-positive={statusGreen(order, order.display_state)}
                    class:fizz-text-subdued={statusGray(order, order.display_state)}
                    >{order.display_state}</span
                  >
                {/if}
              </p>
              <Arrow direction={"right"} />
            </div>
          </div>
        </div>
        {#if order.sales_reps.length}
          <div class="grid-row" id="order_{order.id}_sales_reps">
            <div class="grid-col c7-10">
              <span class="default-small--bold">REPS: </span>
              {#each order.sales_reps as rep}
                <div>
                  <span class="notranslate">{rep.name}:</span>
                  <span>
                    {#if rep.contact_phone}
                      <a
                        class="accent-body--bold underline-hover cursor-pointer notranslate"
                        href="tel:{rep.contact_phone}"
                        on:click|stopPropagation>{rep.contact_phone}</a
                      >
                    {/if}
                    {#if rep.contact_phone && rep.contact_email}<span>|</span>{/if}
                    {#if rep.contact_email}
                      <a
                        class="accent-body--bold underline-hover cursor-pointer notranslate"
                        href="mailto:{rep.contact_email}"
                        on:click|stopPropagation>{rep.contact_email}</a
                      >
                    {/if}
                  </span>
                </div>
              {/each}
            </div>
            {#if !mobileMode && order.state == "accepted" && order.in_stock_count < order.product_count}
              <div class="grid-col c3-10 fizz-flex-row-inline fizz-justify-end">
                <div class="fizz-tooltip-container" style="margin-right: 1rem;">
                  <span
                    class="underline fizz-text-subdued fizz-tooltip-trigger"
                    aria-describedby="items-accepted"
                  >
                    {OrderService.orderInStockItemsAccepted(order)}
                  </span>
                  <div
                    class="fizz-tooltip fizz-tooltip-right fizz-inset-square-16"
                    role="tooltip"
                    id="items-accepted"
                    style="--max-width:180px; padding-bottom: 1.25rem;"
                  >
                    This distributor's ordering system is connected directly to Provi and able to
                    pass along order statuses.
                  </div>
                </div>
              </div>
            {/if}
          </div>
        {/if}
      </UIRouterLink>
    {/each}

    <div
      class="order-index__order-footer grid-row padding--smaller padding-right--small--sm padding-left--small--sm"
    >
      <div class="grid-col c12-12 vertical-align-middle align-right">
        <button
          class="button button--secondary button--smallest margin-right--smallest--sm"
          on:click={() => download(orderGroup.id)}
        >
          Print Order
        </button>
      </div>
    </div>
  </div>
</div>
